import {
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  getDocFromServer,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, googleProvider, db } from '../config/firebase';
import { COLLECTIONS } from '../config/constants';
import { AuthError, FirestoreError, mapFirebaseAuthError } from '../lib/errors';

/**
 * Detecta si conviene usar redirect en lugar de popup.
 * iOS Safari y algunos in-app browsers fallan con popup.
 */
const shouldUseRedirect = () => {
  const ua = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isInApp = /fban|fbav|instagram|line|wechat/.test(ua);
  return isIOS || isInApp;
};

/**
 * Lee un doc forzando el server, con fallback a la caché si estamos offline.
 *
 * Crítico para el perfil del usuario: `familyId` decide el routing
 * (onboarding vs hogar). La caché persistente (persistentLocalCache) puede
 * tener un `familyId` stale —típicamente `null` de antes de unirse a un
 * hogar, o de la migración de proyecto— y mandar a onboarding a alguien que
 * ya tiene hogar. Leer del server elimina ese race. Si el server no está
 * accesible (offline), caemos a la caché para no romper el login.
 */
const getDocServerFirst = async (ref) => {
  try {
    return await getDocFromServer(ref);
  } catch {
    return getDoc(ref);
  }
};

/**
 * Crea o actualiza el documento del usuario en Firestore y devuelve el perfil.
 * Idempotente: seguro de llamar en cada login y en cada onAuthStateChanged.
 *
 * Es la ÚNICA vía de bootstrap del perfil. AuthContext la llama desde
 * onAuthStateChanged, así que cubre popup y redirect por igual y sin races
 * (antes el bootstrap vivía en signInWithGoogle y onAuthStateChanged podía
 * leer el perfil antes de que existiera).
 */
export const upsertUserProfile = async (firebaseUser) => {
  const userRef = doc(db, COLLECTIONS.USERS, firebaseUser.uid);

  try {
    const snapshot = await getDocServerFirst(userRef);

    if (!snapshot.exists()) {
      // Primer login: bootstrap completo.
      await setDoc(userRef, {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || 'Usuario',
        photoURL: firebaseUser.photoURL || null,
        familyId: null, // Se asigna en onboarding
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        fcmTokens: [],
      });
      // Re-leemos para devolver el doc con los serverTimestamp ya resueltos.
      const created = await getDoc(userRef);
      return created.data();
    }

    // Login recurrente: solo actualizar metadatos volátiles.
    const existing = snapshot.data();
    const synced = {
      lastLoginAt: serverTimestamp(),
      // Sincroniza cambios de Google (foto, nombre).
      displayName: firebaseUser.displayName || existing.displayName,
      photoURL: firebaseUser.photoURL || existing.photoURL,
    };
    await setDoc(userRef, synced, { merge: true });

    // Devolvemos el perfil con los cambios aplicados localmente. lastLoginAt
    // en memoria queda con el valor previo hasta el próximo fetch, pero
    // uid/familyId (lo que importa para el flujo) ya son correctos.
    return {
      ...existing,
      displayName: synced.displayName,
      photoURL: synced.photoURL,
    };
  } catch (err) {
    throw new FirestoreError('No se pudo sincronizar el perfil de usuario', err);
  }
};

/**
 * Inicia sesión con Google. Maneja popup/redirect según el entorno.
 * El bootstrap del perfil NO ocurre acá — lo hace AuthContext vía
 * onAuthStateChanged, que dispara tanto para popup como para redirect.
 */
export const signInWithGoogle = async () => {
  try {
    if (shouldUseRedirect()) {
      // signInWithRedirect recarga la página; el usuario llega vía
      // onAuthStateChanged después del redirect.
      await signInWithRedirect(auth, googleProvider);
      return;
    }
    await signInWithPopup(auth, googleProvider);
  } catch (err) {
    throw new AuthError(mapFirebaseAuthError(err), err);
  }
};

/**
 * Cierra sesión limpiamente.
 */
export const signOut = async () => {
  try {
    await firebaseSignOut(auth);
  } catch (err) {
    throw new AuthError('No se pudo cerrar sesión', err);
  }
};

/**
 * Recupera el perfil completo desde Firestore.
 * Necesario porque Firebase Auth no incluye `familyId`.
 */
export const fetchUserProfile = async (uid) => {
  try {
    const snapshot = await getDocServerFirst(doc(db, COLLECTIONS.USERS, uid));
    return snapshot.exists() ? snapshot.data() : null;
  } catch (err) {
    throw new FirestoreError('No se pudo cargar el perfil', err);
  }
};

/**
 * Suscriptor reactivo al estado de auth.
 * Retorna unsubscribe.
 */
export const subscribeToAuthChanges = (callback) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Limpia el propio `users/{uid}.familyId = null`. Operación self-update
 * (las rules permiten solo `isSelf(userId)` para writes en users).
 *
 * Se usa cuando el cliente detecta que perdió acceso al hogar al que
 * creía pertenecer:
 *  - El owner lo expulsó (`kickMember`).
 *  - El hogar fue eliminado por el owner.
 *
 * En esos casos `users.familyId` queda stale apuntando a un hogar donde
 * el user ya no es miembro. El listener de subscribeToFamily falla con
 * `permission-denied`. FamilyContext detecta ese error y llama acá para
 * limpiar el campo y forzar el redirect a /onboarding.
 *
 * NO es lo mismo que `leaveFamily`: aquí el caller ya no es miembro, así
 * que NO toca `families/{familyId}` (no podría aunque quisiera). Solo
 * limpia su propio profile.
 */
export const clearOwnFamilyId = async (uid) => {
  if (!uid) throw new AuthError('uid requerido');
  try {
    await updateDoc(doc(db, COLLECTIONS.USERS, uid), {
      familyId: null,
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    throw new FirestoreError('No se pudo limpiar el hogar del perfil', err);
  }
};